import React, { useState, useEffect } from 'react';
import { LinkSubmissionForm } from './LinkSubmissionForm';
import { LinkList } from './LinkList';
import { LinkCategories } from './LinkCategories';
import { CourseIdeaGenerator } from './CourseIdeaGenerator';

export interface SharedLink {
  id: number;
  title: string;
  url: string;
  description?: string;
  category: string;
  tags: string[];
  user_id: number;
  user_name?: string;
  likes: number;
  views: number;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

export interface Category {
  category: string;
  count: number;
}

export const LinkSharing: React.FC = () => {
  const [links, setLinks] = useState<SharedLink[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [showCourseGenerator, setShowCourseGenerator] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchLinks = async (category?: string, featured?: boolean) => {
    try {
      const params = new URLSearchParams();
      if (category && category !== 'all') params.append('category', category);
      if (featured) params.append('featured', 'true');
      
      const response = await fetch(`/api/links?${params.toString()}`);
      const data = await response.json();
      
      if (response.ok) {
        setLinks(data.links);
      } else {
        console.error('Failed to fetch links:', data.error);
      }
    } catch (error) {
      console.error('Error fetching links:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/categories');
      const data = await response.json();
      
      if (response.ok) {
        setCategories(data.categories);
      } else {
        console.error('Failed to fetch categories:', data.error);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLinks(selectedCategory), fetchCategories()]);
      setLoading(false);
    };
    
    loadData();
  }, [selectedCategory]);

  const handleLinkSubmitted = () => {
    setShowForm(false);
    fetchLinks(selectedCategory);
    fetchCategories();
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  const handleLikeLink = async (linkId: number) => {
    try {
      const response = await fetch(`/api/links/${linkId}/like`, {
        method: 'PUT',
      });
      
      if (response.ok) {
        setLinks(links.map(link => 
          link.id === linkId 
            ? { ...link, likes: link.likes + 1 }
            : link
        ));
      }
    } catch (error) {
      console.error('Error liking link:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        <header className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            🚀 Knowledge Sharing Hub
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            Share and discover amazing resources to amplify our collective knowledge
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            <button
              onClick={() => {
                setShowForm(!showForm);
                setShowCourseGenerator(false);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2"
            >
              <span>🔗</span>
              <span>{showForm ? 'Cancel' : 'Share a Link'}</span>
            </button>
            
            <button
              onClick={() => {
                setShowCourseGenerator(!showCourseGenerator);
                setShowForm(false);
              }}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
            >
              <span>🎓</span>
              <span>{showCourseGenerator ? 'Hide Generator' : 'AI Course Ideas'}</span>
            </button>
            
            <button
              onClick={() => handleCategoryChange('')}
              className={`px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2 ${
                selectedCategory === '' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>📚</span>
              <span>All Links</span>
            </button>
            
            <button
              onClick={() => fetchLinks('', true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2"
            >
              <span>⭐</span>
              <span>Featured</span>
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <LinkCategories 
              categories={categories}
              selectedCategory={selectedCategory}
              onCategorySelect={handleCategoryChange}
            />
          </div>
          
          <div className="lg:col-span-3">
            {showCourseGenerator && (
              <div className="mb-8">
                <CourseIdeaGenerator />
              </div>
            )}
            
            {showForm && (
              <div className="mb-8">
                <LinkSubmissionForm 
                  onLinkSubmitted={handleLinkSubmitted}
                  categories={categories.map(c => c.category)}
                />
              </div>
            )}
            
            {!showCourseGenerator && (
              <LinkList 
                links={links}
                loading={loading}
                onLikeLink={handleLikeLink}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};